import { createTheme } from "@mui/material/styles";

// Emaan Reviver - Original Color Scheme Preserved
// Migrated from Bootstrap to Material UI while maintaining the original design
const theme = createTheme({
    palette: {
        mode: "light",
        // Original primary green color from Bootstrap theme
        primary: {
            main: "#0c6e37", // Original $primary-color
            light: "#31e981", // Original $secondary-color
            dark: "#0a5a2d",
            contrastText: "#fff",
        },
        // Secondary purple/blue color used in feedback buttons
        secondary: {
            main: "#636b9f", // Original feedback button color
            light: "#7e85b3",
            dark: "#4e5580",
            contrastText: "#fff",
        },
        // Error/Invalid color from original theme
        error: {
            main: "#ff4242", // Original $red-color
            light: "#ff6b6b",
            dark: "#dc3545", // Original $invalid-color
            contrastText: "#fff",
        },
        // Warning stays similar
        warning: {
            main: "#f0ad4e",
            light: "#f4bd6e",
            dark: "#ec9c3f",
            contrastText: "#fff",
        },
        // Info blue from original theme
        info: {
            main: "#0996d7", // Original $blue
            light: "#03a9f4",
            dark: "#03256c", // Original $dark-blue
            contrastText: "#fff",
        },
        // Success green from original theme
        success: {
            main: "#28a745", // Original $green-color / $valid-color
            light: "#6b8a4e", // Original share button color
            dark: "#1e7e34",
            contrastText: "#fff",
        },
        grey: {
            50: "#f2f2f7", // Original $background-color
            100: "#e6e6e6", // Original $items-background
            200: "#e1e1ea", // Original $background
            300: "#a1a8c3", // Original $placeholder-color
            400: "#8e8e8e", // Original $span-color
            500: "#8a8a91", // Original $secondary-text-color
            600: "#646c9a", // Original $label-color
            700: "#495057", // Original $default-text-color
            800: "#434349", // Original $heading-color
            900: "#222222", // Original $black-color
        },
        background: {
            default: "#f2f2f7", // Original $background-color
            paper: "#ffffff", // Original $white-color
        },
        text: {
            primary: "#383a3f", // Original $primary-text-color
            secondary: "#8a8a91", // Original $secondary-text-color
            disabled: "#a1a8c3", // Original $placeholder-color
        },
        divider: "rgba(227, 229, 234, 0.48)", // Original $box-shadow-color
        // Custom colors for specific use cases
        action: {
            active: "#0c6e37",
            hover: "rgba(12, 110, 55, 0.08)",
            selected: "rgba(12, 110, 55, 0.16)",
            disabled: "#a1a8c3",
            disabledBackground: "#e6e6e6",
        },
    },
    typography: {
        // Original font family - Poppins
        fontFamily: [
            '"Poppins"',
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
        ].join(","),
        h1: {
            fontSize: "2.5rem",
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: "-0.01562em",
        },
        h2: {
            fontSize: "2rem",
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: "-0.00833em",
        },
        h3: {
            fontSize: "1.75rem",
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: "0em",
        },
        h4: {
            fontSize: "1.5rem",
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: "0.00735em",
        },
        h5: {
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: 1.5,
            letterSpacing: "0em",
        },
        h6: {
            fontSize: "1.125rem",
            fontWeight: 600,
            lineHeight: 1.6,
            letterSpacing: "0.0075em",
        },
        subtitle1: {
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.75,
            letterSpacing: "0.00938em",
        },
        subtitle2: {
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.57,
            letterSpacing: "0.00714em",
        },
        body1: {
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: "0.00938em",
        },
        body2: {
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.43,
            letterSpacing: "0.01071em",
        },
        button: {
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.75,
            letterSpacing: "0.02857em",
            textTransform: "uppercase",
        },
        caption: {
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: 1.66,
            letterSpacing: "0.03333em",
        },
        overline: {
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: 2.66,
            letterSpacing: "0.08333em",
            textTransform: "uppercase",
        },
    },
    spacing: 8,
    shape: {
        borderRadius: 8,
    },
    shadows: [
        "none",
        "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
        "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
        "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
        "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
        "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
        "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
        "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
        "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
        "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
        "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
        "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
        "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
        "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
        "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
        "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
        "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
        "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
        "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
        "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
        "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
        "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
        "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
        "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
        "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)",
    ],
    transitions: {
        easing: {
            easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
            easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
            easeIn: "cubic-bezier(0.4, 0, 1, 1)",
            sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
        },
        duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    zIndex: {
        mobileStepper: 1000,
        speedDial: 1050,
        appBar: 1100,
        drawer: 1200,
        modal: 1300,
        snackbar: 1400,
        tooltip: 1500,
    },
    components: {
        // Global focus styles for all MUI components
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "14px",
                    padding: "8px 20px",
                    transition: "all 0.3s ease",
                    boxShadow: "none",
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                    "&:hover": {
                        transform: "translateY(-1px)",
                    },
                    "&:active": {
                        transform: "translateY(0)",
                    },
                },
                // Primary (Green) - Contained
                containedPrimary: {
                    backgroundColor: "#0c6e37",
                    color: "#fff",
                    boxShadow: "0 2px 4px rgba(12, 110, 55, 0.2)",
                    "&:hover": {
                        backgroundColor: "#0a5a2d",
                        boxShadow: "0 4px 8px rgba(12, 110, 55, 0.3)",
                    },
                    "&:active": {
                        boxShadow: "0 1px 2px rgba(12, 110, 55, 0.3)",
                    },
                },
                // Secondary (Outlined)
                outlined: {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    color: "#383a3f",
                    "&:hover": {
                        backgroundColor: "rgba(12, 110, 55, 0.04)",
                        borderColor: "#0c6e37",
                        color: "#0c6e37",
                    },
                },
                // Success (Green)
                containedSuccess: {
                    backgroundColor: "#28a745",
                    color: "#fff",
                    boxShadow: "0 2px 4px rgba(40, 167, 69, 0.2)",
                    "&:hover": {
                        backgroundColor: "#218838",
                        boxShadow: "0 4px 8px rgba(40, 167, 69, 0.3)",
                    },
                },
                // Error/Delete (Red)
                containedError: {
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    boxShadow: "0 2px 4px rgba(211, 47, 47, 0.2)",
                    "&:hover": {
                        backgroundColor: "#b71c1c",
                        boxShadow: "0 4px 8px rgba(211, 47, 47, 0.3)",
                    },
                },
                outlinedError: {
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                    borderColor: "rgba(211, 47, 47, 0.5)",
                    color: "#d32f2f",
                    "&:hover": {
                        backgroundColor: "#d32f2f",
                        borderColor: "#d32f2f",
                        color: "#fff",
                        boxShadow: "0 4px 12px rgba(211, 47, 47, 0.4)",
                    },
                },
                // Warning (Orange)
                containedWarning: {
                    backgroundColor: "#f57c00",
                    color: "#fff",
                    boxShadow: "0 2px 4px rgba(245, 124, 0, 0.2)",
                    "&:hover": {
                        backgroundColor: "#e65100",
                        boxShadow: "0 4px 8px rgba(245, 124, 0, 0.3)",
                    },
                },
                outlinedWarning: {
                    backgroundColor: "rgba(255, 152, 0, 0.08)",
                    borderColor: "rgba(255, 152, 0, 0.5)",
                    color: "#f57c00",
                    "&:hover": {
                        backgroundColor: "#f57c00",
                        borderColor: "#f57c00",
                        color: "#fff",
                        boxShadow: "0 4px 8px rgba(245, 124, 0, 0.3)",
                    },
                },
            },
            defaultProps: {
                disableElevation: false,
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 5, // Match original border radius
                        "&:hover fieldset": {
                            borderColor: "#0c6e37",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#0c6e37", // Original primary color on focus
                        },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                        color: "#0c6e37", // Label color when focused
                    },
                    // Ensure disabled inputs have black text
                    "& .MuiInputBase-input.Mui-disabled": {
                        color: "#1a1a1a",
                        WebkitTextFillColor: "#1a1a1a",
                    },
                    "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#383a3f",
                    },
                },
            },
            defaultProps: {
                variant: "outlined",
                fullWidth: true,
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    // Original box shadow style
                    boxShadow:
                        "13px 13px 20px rgba(227, 229, 234, 0.48), -13px -13px 20px rgba(227, 229, 234, 0.48)",
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 24px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                    "& .MuiTypography-root": {
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: "24px",
                    color: "#1a1a1a", // Ensure black text in modals
                    "& .MuiTypography-root": {
                        color: "#1a1a1a", // Black text for all Typography in dialogs
                    },
                    "&.MuiDialogContent-dividers": {
                        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                    },
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: "12px 24px",
                    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                    gap: "8px",
                    "& .MuiButton-root": {
                        minWidth: "80px",
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    "&:focus:not(:focus-visible)": {
                        outline: "none",
                        boxShadow: "none",
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                standardSuccess: {
                    backgroundColor: "rgba(46, 125, 50, 0.1)",
                    color: "#1b5e20",
                },
                standardError: {
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                    color: "#c62828",
                },
                standardWarning: {
                    backgroundColor: "rgba(237, 108, 2, 0.1)",
                    color: "#e65100",
                },
                standardInfo: {
                    backgroundColor: "rgba(2, 136, 209, 0.1)",
                    color: "#01579b",
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                    backgroundColor: "#fff",
                    "&::-webkit-scrollbar": {
                        width: 6,
                        height: 6,
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f5f5f5",
                        borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: 3,
                        "&:hover": {
                            backgroundColor: "#a8a8a8",
                        },
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    backgroundColor: "#fff",
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: "#f5f5f5",
                    "& .MuiTableCell-head": {
                        backgroundColor: "#f5f5f5",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        color: "#383a3f",
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                        borderBottom: "2px solid rgba(0, 0, 0, 0.08)",
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    "& .MuiTableRow-root": {
                        transition: "background-color 0.2s ease-in-out",
                        "&:nth-of-type(odd)": {
                            backgroundColor: "#fafafa",
                        },
                        "&:nth-of-type(even)": {
                            backgroundColor: "#fff",
                        },
                        "&:hover": {
                            backgroundColor: "#f0f7f4 !important",
                        },
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&.MuiTableRow-hover:hover": {
                        backgroundColor: "#f0f7f4 !important",
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                    padding: "12px 16px",
                    fontSize: "0.8125rem",
                    color: "#383a3f",
                    verticalAlign: "middle",
                },
                head: {
                    fontWeight: 600,
                    backgroundColor: "#f5f5f5",
                    padding: "12px 16px",
                    color: "#383a3f",
                    borderBottom: "2px solid rgba(0, 0, 0, 0.08)",
                },
                sizeSmall: {
                    padding: "8px 12px",
                    "&.MuiTableCell-head": {
                        padding: "10px 12px",
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
                elevation1: {
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: "1px solid rgba(0, 0, 0, 0.08)",
                },
            },
        },
    },
});

export default theme;
